import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebase';
import {
	doc,
	getDoc,
	updateDoc,
	Timestamp,
	collection,
	query,
	orderBy,
	onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { useTestMode } from '../../../contexts/TestModeContext';
import { filterActiveStudents } from '../../../utils/studentFilters';
import { isPermissionDenied } from '../../../utils/permissions';
import { createTask } from '../../../services/TaskService';
import { sendLessonNotes } from '../../../utils/emailService';
import AccessNotice from '../../../components/Portal/AccessNotice';

import useSongBoard from './hooks/useSongBoard';
import useLessonScratch from './hooks/useLessonScratch';
import { useLessonSession } from '../../../contexts/LessonSessionContext';
import useSongNotes from './hooks/useSongNotes';
import useCarryForward from './hooks/useCarryForward';
import useFamilyQuestions from './hooks/useFamilyQuestions';
import useDeliverNote from './hooks/useDeliverNote';
import { carryOpenDefault } from './notes/CarryForward';
import usePreviousRecaps from './hooks/usePreviousRecaps';
import usePrivateNotes from './hooks/usePrivateNotes';
import useLessonTimer from './hooks/useLessonTimer';
import { setLessonOutcome } from '../../../services/lessonOutcome';
import useRecapDrafts from './hooks/useRecapDrafts';
import useRecapSaveState from './hooks/useRecapSaveState';
import useLyrics from './hooks/useLyrics';
import useSongSections from './hooks/useSongSections';
import useSongVideo from './hooks/useSongVideo';
import useWarmupRoutine from './hooks/useWarmupRoutine';
import useMinWidth from './hooks/useMinWidth';
import useWarmupPlayer from '../../../components/Warmups/useWarmupPlayer';
import useWarmupRoutinePlayer from '../../../components/Warmups/useWarmupRoutinePlayer';
import useTrackPlayer from '../../../components/Tracks/useTrackPlayer';
import useStudentTracks from '../../../components/Tracks/useStudentTracks';
import useTrackJobs from '../../../components/Tracks/useTrackJobs';
import useVideoTrack from './hooks/useVideoTrack';
import useSongReuse from './hooks/useSongReuse';
import { canManageSongContent } from '../../../utils/permissions';
import useTrackWorkers from '../../../components/Tracks/useTrackWorkers';
import useStageLyrics from './stage/useStageLyrics';
import { timedLyricsOf } from './stage/stageModel';
import { confirmTrackKey, findTrackKey } from '../../../services/TrackKeysService';
import { markKeyFor } from './tracks/sectionMarkers';
import { setSectionMark, setTrackTrim } from '../../../services/RepertoireService';
import useTrackRoles from './hooks/useTrackRoles';
import { saveStudentKey, saveTrackCut, linkForSavedVideo, stopTrackJob } from '../../../services/TrackJobsService';
import { canStopJob, cutSetFor, jobStatusText, jobTone, jobsForSong, macsOn, removeVocalsForVideo, visibleJobs } from '../../../services/trackJobsModel';
import { vocalProgress } from './tracks/trackStripModel';
import { playbackUrlFor, trackDocsForSong, tracksForSong, unmatchedTrackDocs } from '../../../services/studentTracksModel';
import { pickLyricTrack } from './songpanel/lyricLineTimes';
import { nowPlayingFile } from './songpanel/nowPlayingModel';
import ConductingStrip from './warmups/ConductingStrip';
import RecapDrawer from './recap/RecapDrawer';
import { WARMUPS_SONG_ID } from '../../../services/songNotesModel';

import LessonTopbar from './spine/LessonTopbar';
import NotesColumn from './spine/NotesColumn';
import ContextRail from './spine/ContextRail';
import PrivateFile from './private/PrivateFile';
import BottomSheet from './sheets/BottomSheet';
import { staffFirstName } from '../../../services/PrivateNotesService';
import RightColumn from './spine/RightColumn';
import JotBar from './notes/JotBar';
import SectionChips from './notes/SectionChips';
import SectionsSheet from './sheets/SectionsSheet';
import DeliverDialog from './notes/DeliverDialog';
import WrapUpSheet from './recap/WrapUpSheet';
import { buildRecapEmailPayload } from './recap/recapEmail';
import AddSongSheet from './sheets/AddSongSheet';
import StudentDrawer from './sheets/StudentDrawer';
import TrackStrip from './tracks/TrackStrip';
import SongPanel from './songpanel/SongPanel';
import SongPanelSheet from './songpanel/SongPanelSheet';
import LyricsPane from './songpanel/LyricsPane';
import TracksPane from './songpanel/TracksPane';
import SongSetup from './songpanel/SongSetup';
import { resetStage } from './stage/lessonStage';
import useStageVideoClock from './stage/useStageVideoClock';
import { getYoutubeVideoId } from './youtubeVideo';
import VideosPane from './songpanel/VideosPane';
import QuickAddTaskDialog from './tasks/QuickAddTaskDialog';

import './LessonDashboard.css';

/** "Tue 4:30 · Voice 30 min" from the lesson doc. */
const buildSubLine = (lesson) => {
	if (!lesson) return '';
	const parts = [];
	if (lesson.date) {
		const d = new Date(`${lesson.date}T00:00:00`);
		if (!Number.isNaN(d.getTime())) {
			const day = d.toLocaleDateString('en-US', { weekday: 'short' });
			const time = lesson.time
				? (() => {
						const [h, m] = lesson.time.split(':');
						const hour = parseInt(h, 10);
						return `${hour % 12 || 12}:${m}`;
				  })()
				: '';
			parts.push([day, time].filter(Boolean).join(' '));
		}
	}
	const kind = lesson.lessonType || lesson.type || '';
	const dur = lesson.duration ? `${lesson.duration} min` : '';
	const kd = [kind, dur].filter(Boolean).join(' ');
	if (kd) parts.push(kd);
	return parts.join(' · ');
};

const LessonDashboard = () => {
	const { lessonId } = useParams();
	const navigate = useNavigate();
	const { currentUser, userProfile } = useAuth();
	const { testMode } = useTestMode();

	const [lesson, setLesson] = useState(null);
	const [student, setStudent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [lessonDenied, setLessonDenied] = useState(false);
	const [students, setStudents] = useState([]);

	// Which overlay is up: 'add-song' | 'lyrics' | 'recap' | 'wrap-up' | 'drawer'
	// | null. (The note in the Deliver dialog is useDeliverNote's.)
	const [activeSheet, setActiveSheet] = useState(null);
	// Private file History: the rail's and the sheet's are separate states, so
	// opening History in the sheet at 236px never re-lays the rail behind it.
	const [privateRailHistory, setPrivateRailHistory] = useState(false);
	const [privateSheetHistory, setPrivateSheetHistory] = useState(false);
	// Pass 3 A3: the Sections sheet opened FROM the Lyrics sheet stacks on it
	// (Lyrics stays mounted underneath, z 1040 under BottomSheet's 1050), so
	// Save, Discard and close all return to the Lyrics sheet. Opened from
	// anywhere else it is an ordinary activeSheet. Declared here, above every
	// memo and callback that reads it (Babel removes the TDZ).
	const [sectionsOverLyrics, setSectionsOverLyrics] = useState(false);
	// Pass 3 D1/D2: the Song panel's mode, ONE per lesson (default Lyrics),
	// shared by the desktop column and the sheet below 1200, so the strip's
	// "All tracks" and the tool row can switch it from anywhere.
	const [songPanelMode, setSongPanelMode] = useState('lyrics');
	// Pass 3 D3/F3: what Add a track opens with ({ url?, removeVocals?,
	// label?, nonce }); a new nonce re-applies it. null = closed.
	const [addPreset, setAddPreset] = useState(null);
	// A Stop tapped on the Song panel's Tracks row opens Tracks mode with that
	// job's confirmation already asked (pass 4 S5.1).
	const [stopAsk, setStopAsk] = useState(null);
	// The song just added that is still being set up (pass 4 S3): its lyric
	// video search and the Yes / Later question. Null once done or put off.
	const [setupSongId, setSetupSongId] = useState(null);
	// The stage's video clock (pass 4 S4): lyrics follow a lyric video playing there.
	const stageVideo = useStageVideoClock();
	// Pass 3 F3/F4: dismissed track-job ids, shared by the strip's job chip
	// and Tracks mode's Working now, so a dismissed failure clears from both.
	const [dismissedJobs, setDismissedJobs] = useState(() => new Set());
	const [taskDialogOpen, setTaskDialogOpen] = useState(false);
	// Where the Recap drawer should land when it opens: null (the editors) or
	// 'previous' (the Previous recaps disclosure, open and scrolled into view;
	// the Last lesson row asks for that). Cleared when the drawer closes.
	const [recapFocus, setRecapFocus] = useState(null);
	const openRecap = useCallback(() => {
		setRecapFocus(null);
		setActiveSheet('recap');
	}, []);
	const openPreviousRecaps = useCallback(() => {
		setRecapFocus('previous');
		setActiveSheet('recap');
	}, []);
	const closeSheet = useCallback(() => {
		setRecapFocus(null);
		setActiveSheet(null);
	}, []);
	// Carry-forward Show / Collapse (design pass 2, change 4): ONE state for the
	// page, read by both mounts (rail and notes column) so a teacher who
	// collapses it on one tab finds it collapsed on the next. null = not yet
	// chosen. Pass 3 C3 replaced the width default (open on iPad and desktop,
	// collapsed on phone) with a LESSON default: open until the timer starts
	// or the first jot, then collapsed, at every width (carryOpenDefault).
	// Declared above every memo that reads it (CLAUDE.md, the
	// use-before-declare trap).
	const [carryOpenChoice, setCarryOpenChoice] = useState(null);
	const [hasJotted, setHasJotted] = useState(false);

	// Bumped on any note persist so carry-forward re-reads (notes live in their
	// own documents, so the two surfaces would otherwise drift within a lesson).
	const [carryForwardToken, setCarryForwardToken] = useState(0);
	const bumpCarryForward = useCallback(() => setCarryForwardToken((t) => t + 1), []);

	// Lock the document while a lesson is open — below Ph 7 (≥1200px, not yet
	// built) .ld-shell takes over the viewport (LessonDashboard.css), and iOS
	// still rubber-bands an unlocked body underneath a position:fixed shell in
	// edge cases (address bar show/hide, keyboard open/close). Matches the CSS
	// breakpoint: Ph 7's desktop composes with the portal topbar instead of
	// covering it, so locking body there would make anything taller than one
	// viewport unreachable.
	useEffect(() => {
		// >=1200px composes with the portal chrome, and until 2026-09-21 nothing
		// bounded the shell's height there: .ld-shell is min-height:100% inside
		// an auto-height .portal-content, so .ld-main never became the scroll
		// region, the DOCUMENT scrolled, and the in-flow jot bar sat at the foot
		// of the tallest column, thousands of pixels below the fold (Anthony:
		// "it gets lost outside the view height, and the only way to get it back
		// is to scroll on the left side of the page"). This class lets
		// LessonDashboard.css bound the chrome to the viewport for the lesson
		// route alone, so the bar is at the window's bottom at every width and
		// in both sidebar states without a single pixel constant
		// (scripts/measureJotBarPinned.js measures it).
		document.body.classList.add('ld-lesson-open');
		return () => document.body.classList.remove('ld-lesson-open');
	}, []);
	useEffect(() => {
		if (!window.matchMedia('(max-width: 1199px)').matches) return undefined;
		const { overflow, position, width, height } = document.body.style;
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'fixed';
		document.body.style.width = '100%';
		document.body.style.height = '100%';
		return () => {
			document.body.style.overflow = overflow;
			document.body.style.position = position;
			document.body.style.width = width;
			document.body.style.height = height;
		};
	}, []);

	const studentId = lesson?.studentId || null;

	// Scratch = the two things that were plain component state and simply
	// vanished on a route change: the active song tab and any unsent jot.
	// Everything else in a lesson is already durable (see useLessonScratch).
	const { scratch, patchScratch, clearScratch } = useLessonScratch(lessonId);
	const { setOpenLesson, clearOpenLesson } = useLessonSession();
	const board = useSongBoard({ studentId, initialSongId: scratch.currentSongId });
	const notes = useSongNotes({
		board,
		lessonId,
		createdBy: currentUser?.uid || null,
		onNotesPersisted: bumpCarryForward,
	});
	const carryForward = useCarryForward({
		studentId,
		refreshToken: carryForwardToken,
	});
	// From home, page-owned since pass 3 C2 (both Arrive mounts share it).
	const familyQuestions = useFamilyQuestions({ studentId, currentTeacherId: currentUser?.uid || null });
	const timer = useLessonTimer({ lessonId, lesson, setLesson });
	// First word of the display name only; never the email, which the other
	// fallbacks in this file reach for and which a family must not be shown.
	const authorFirstName = ((userProfile?.displayName || currentUser?.displayName || '').trim().split(/\s+/)[0]) || null;
	const recap = useRecapDrafts({ lessonId, lesson, student, currentUser, authorFirstName });
	const recapSaveState = useRecapSaveState(recap);
	// Earlier recaps for this student, read ONCE here and handed to the drawer:
	// the page owns it so tab switches and drawer open/close never refetch.
	const previousRecaps = usePreviousRecaps({ studentId, currentLessonId: lessonId });
	const privateNotes = usePrivateNotes(student?.id || null);
	const lyrics = useLyrics({
		currentSong: board.currentSong,
		setSongs: board.setSongs,
		isPersistent: board.isPersistent,
		uid: currentUser?.uid || null,
	});
	// Song sections (2026-09-25): the chips over the jot bar, the grouped list,
	// the labels in the lyrics. Declared here, above every memo/handler that
	// reads it (CLAUDE.md: a const read above its declaration is `undefined`).
	const songSections = useSongSections({ board });
	const video = useSongVideo({
		currentSong: board.currentSong,
		setSongs: board.setSongs,
		isPersistent: board.isPersistent,
	});

	// Library tracks (2026-09-16). ONE player for the page — same reason the
	// warmup engine and the RecapDrawer are lifted here: pills render in the
	// notes column at every width, and a second player anywhere would be two
	// cuts able to play over each other. The pointer docs are one read per
	// student; the current tab's files are derived, never stored.
	const trackPlayer = useTrackPlayer();
	const studentTracks = useStudentTracks(studentId);
	// "Add a track" (2026-09-26): the student's track jobs, live; a job that
	// lands re-reads the pointers so the new cut appears in this lesson.
	const trackJobs = useTrackJobs(studentId, studentTracks.reload);
	// …and which studio Macs are on (build step 2), only while a song tab is
	// open for a student: the Add a track control is the only reader.
	const trackWorkers = useTrackWorkers(Boolean(studentId && board.currentSong && board.currentSong.id !== WARMUPS_SONG_ID));
	const currentSongTracks = useMemo(
		() => tracksForSong(studentTracks.docs, board.currentSong),
		[studentTracks.docs, board.currentSong]
	);
	// …and the filed cuts NO song on the board claims ("Tracks on file",
	// 2026-09-24). Same match predicate, so a track shows in exactly one place.
	const tracksOnFile = useMemo(
		() => unmatchedTrackDocs(studentTracks.docs, board.songs),
		[studentTracks.docs, board.songs]
	);
	// The second screen's scrolling words (2026-09-27): every file a teacher
	// can play on this page, so a saved key can borrow its original's times.
	const playableTrackFiles = useMemo(
		() => [...currentSongTracks, ...tracksOnFile.flatMap((t) => t.files || [])],
		[currentSongTracks, tracksOnFile]
	);
	// A teacher's nudge of a section marker (2026-09-28, tracks/sectionMarkers.js):
	// patched on the board at once, then written as ONE dot path on the song.
	const setSectionMarkFor = useCallback(
		async (file, sectionId, seconds) => {
			const song = board.currentSong;
			const key = markKeyFor(file, playableTrackFiles);
			if (!song || !key) return;
			board.setSongs((prev) =>
				prev.map((s) => {
					if (s.id !== song.id) return s;
					const marks = { ...((s.sectionMarks && s.sectionMarks[key]) || {}) };
					if (seconds === null) delete marks[sectionId];
					else marks[sectionId] = Math.max(0, Math.round(Number(seconds) * 10) / 10);
					return { ...s, sectionMarks: { ...(s.sectionMarks || {}), [key]: marks } };
				})
			);
			if (board.isPersistent) await setSectionMark(song.id, key, sectionId, seconds);
		},
		[board, playableTrackFiles]
	);
	// Trim (2026-09-28, tracks/TrimControl.js): saved on the song for the
	// track's timeline, patched on the board at once.
	const saveTrimFor = useCallback(
		async (file, trim) => {
			const song = board.currentSong;
			const key = markKeyFor(file, playableTrackFiles);
			if (!song || !key) return;
			board.setSongs((prev) =>
				prev.map((s) => {
					if (s.id !== song.id) return s;
					const trims = { ...(s.trims || {}) };
					if (trim) trims[key] = { start: trim.start || 0, end: trim.end === undefined ? null : trim.end };
					else delete trims[key];
					return { ...s, trims };
				})
			);
			if (board.isPersistent) await setTrackTrim(song.id, key, trim);
		},
		[board, playableTrackFiles]
	);
	// Hand the open file's trim to the player, whichever song it belongs to:
	// the timeline key is unique, so the first song holding it is its song.
	const playingKey = trackPlayer.current && trackPlayer.current.engine === 'audio' ? markKeyFor(trackPlayer.current.file, playableTrackFiles) : null;
	const playingTrim = playingKey ? (board.songs || []).map((s) => s.trims && s.trims[playingKey]).find(Boolean) || null : null;
	const { setBounds } = trackPlayer;
	useEffect(() => {
		setBounds(playingTrim);
	}, [setBounds, playingKey, playingTrim && playingTrim.start, playingTrim && playingTrim.end]); // eslint-disable-line react-hooks/exhaustive-deps
	const stageLyrics = useStageLyrics({ player: trackPlayer, files: playableTrackFiles, docs: studentTracks.docs });
	// Recital / Audition marks for the open song tab (2026-09-27, step 6).
	const [trackRoleError, setTrackRoleError] = useState('');
	const trackRoles = useTrackRoles({
		board,
		uid: currentUser?.uid || null,
		onError: (e) => setTrackRoleError((e && e.message) || 'unknown error'),
		onSuccess: () => setTrackRoleError(''),
	});

	// Warmups. ONE engine, ONE routine draft and ONE transport state for the
	// page — a JS fork, because two mounted copies would be two "current
	// exercise" states over one engine. useMinWidth defaults to desktop where
	// matchMedia is absent (jsdom), the pre-existing three-pane.
	//
	// I1 phase 1 (2026-09-04) LIFTED `useWarmupRoutinePlayer` to this level.
	// It used to run inside WarmupPlayer, which was fine while the deck and the
	// routine were siblings; the conducting strip and the right column are
	// different subtrees, so the state has to sit above both. `controlled: true`
	// is explicit rather than inferred because the routine is null for the first
	// few frames here and inference would read that as uncontrolled.
	const warmupPlayer = useWarmupPlayer();
	const warmupRoutine = useWarmupRoutine({ studentId, student, lessonId, uid: currentUser?.uid || null });
	const isDesktop = useMinWidth(1200);
	const isWideDesktop = useMinWidth(1440);
	// The lyrics column in a window of its own (2026-10-05, Anthony: "detach the
	// scrolling lyric control column"). Desktop only: the pane is portaled into
	// a second browser window (components/PopoutWindow.js), which an iPad or a
	// phone has no use for. Declared here, above the memos that read it.
	const [lyricsDetached, setLyricsDetached] = useState(false);
	// 1024 is the second fork, and it is the design's own iPad width rather than
	// the 768 this was first written at. At 768 a 440px routine column leaves the
	// notes track 328px — the same crushing that phase 1 hit at 1200, and the
	// design never mocked 768 so it never surfaced there either. Below 1024 the
	// routine scrolls in the notes flow instead, full width, with the strip on
	// top. The ONE case where the right column returns below 1200 is 1024-1199 on
	// the Warm-ups tab; a song tab gets the tool row and sheets at every width
	// under 1200.
	const hasRoutineColumn = useMinWidth(1024);
	const lessonStarted = Boolean(lesson?.startedAt || lesson?.completedAt);
	const carryOpen = carryOpenDefault({ choice: carryOpenChoice, started: lessonStarted, jotted: hasJotted });
	const toggleCarry = useCallback(() => setCarryOpenChoice(!carryOpen), [carryOpen]);
	const wp = useWarmupRoutinePlayer({
		catalog: warmupRoutine.catalog,
		player: warmupPlayer,
		controlled: true,
		routine: warmupRoutine.routine,
		onRoutineChange: warmupRoutine.setRoutine,
		onCellChange: warmupRoutine.setCell,
		current: warmupRoutine.current,
		onCurrentChange: warmupRoutine.setCurrent,
		onExerciseStart: warmupRoutine.onExerciseStart,
		onExerciseEnd: warmupRoutine.onExerciseEnd,
	});
	const warmups = useMemo(
		() => ({
			player: warmupPlayer,
			routine: warmupRoutine,
			wp,
			// >=768 the routine has a column of its own; on phone it scrolls in
			// the notes flow beneath the strip.
			mount: hasRoutineColumn ? 'right' : 'notes',
			// Ready = there is something to conduct. The strip is gated on this
			// rather than rendering LessonWarmups' spinner, because a loading
			// message stretched across the top of the lesson is worse than no
			// band at all — the guards belong where the content is.
			ready: Boolean(!warmupRoutine.catalogError && warmupRoutine.hasStudent && !warmupRoutine.loading && warmupRoutine.routine),
		}),
		[warmupPlayer, warmupRoutine, wp, hasRoutineColumn]
	);

	// The Warm-ups tab is the mode switch — there is no other one (design: "the
	// mode switch is the tab"). The strip is the only thing that appears with it.
	const isWarmupsTab = board.currentSong?.id === WARMUPS_SONG_ID;
	// The strip is the Warm-ups tab's transport at EVERY width — that is the point
	// of I1: the key sits in the same place whether the teacher is at a desk, on
	// an iPad at the piano, or holding a phone. It narrows (CSS) but never moves.
	// It never appears on a song tab: the tab is the mode switch, and nothing else
	// changes with it.
	const showStrip = isWarmupsTab && warmups.ready;

	// Fetch the lesson + its student. A rules denial is an access state, not
	// "not found" (matches the old page's #27 fix).
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			try {
				const lessonDoc = await getDoc(doc(db, 'lessons', lessonId));
				if (cancelled) return;
				if (!lessonDoc.exists()) {
					setLoading(false);
					return;
				}
				const lessonData = { id: lessonDoc.id, ...lessonDoc.data() };
				setLesson(lessonData);
				// Deep link from the student profile's lesson history (2026-09-06):
				// `?open=recap` lands on the recap drawer — "leave lesson feedback"
				// after the fact, without hunting for the control. Read from the
				// window rather than a router hook so the mocks in every existing
				// dashboard test stay untouched. Goes through openRecap, the same
				// opener the topbar uses, so the two UI entry points and this link
				// are the only writers of the 'recap' state
				// (recap/recapEntryPoints.test.js).
				try {
					if (new URLSearchParams(window.location.search).get('open') === 'recap') {
						openRecap();
					}
				} catch (e) {
					/* no window search in some harnesses — ignore */
				}
				if (lessonData.studentId) {
					const studentDoc = await getDoc(doc(db, 'students', lessonData.studentId));
					if (!cancelled && studentDoc.exists()) {
						setStudent({ id: studentDoc.id, ...studentDoc.data() });
					}
				}
			} catch (error) {
				if (cancelled) return;
				console.error('Error fetching lesson data:', error);
				if (isPermissionDenied(error)) setLessonDenied(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
	}, [lessonId, openRecap]);

	// Students roster (link-student search; @mentions in the recap editors).
	useEffect(() => {
		const q = query(collection(db, 'students'), orderBy('fullName'));
		const unsub = onSnapshot(
			q,
			(snap) => {
				const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
				setStudents(filterActiveStudents(all, testMode));
			},
			(error) => console.error('Error subscribing to students:', error)
		);
		return () => unsub();
	}, [testMode]);

	const studentName =
		student?.fullName || lesson?.studentName || (student ? '' : '');
	// "Make a track" by a saved video: declared below studentName for the same
	// use-before-declare reason as the effect just after it.
	const videoTrack = useVideoTrack({
		studentId,
		studentName: studentName || null,
		song: board.currentSong && board.currentSong.id !== WARMUPS_SONG_ID ? board.currentSong : null,
		uid: currentUser?.uid || null,
		jobs: trackJobs,
	});
	// ---- Pass 3 (2026-09-28): the track strip and the Song panel ---------
	// Plain values and closures, not hooks, so their order against the hooks
	// above cannot matter; every piece of state they read is declared above.
	const isPhoneWidth = !useMinWidth(768);
	// THE PRIVATE FILE (pass 4 P2-P4, 2026-10-04). One listener, owned here,
	// read by the rail, the sheet and the lesson bar's count. Where it opens:
	// the rail is painted at 768+ except on Warm-ups with the strip up below
	// 1440 (ContextRail.css + LessonDashboard.css; these two booleans mirror
	// those rules), and at 768-1199 it is 236px, too narrow for History's
	// filter chips (P4.2), so History opens in the sheet there.
	const railShown = !isPhoneWidth && !(showStrip && !isWideDesktop);
	const railRoomy = railShown && isDesktop;
	const privateFileProps = {
		lessonId,
		entries: privateNotes.entries,
		isLoading: privateNotes.isLoading,
		error: privateNotes.error,
		currentUid: currentUser?.uid || null,
		authorName: staffFirstName(currentUser?.uid) || authorFirstName,
		canManage: canManageSongContent(userProfile),
	};
	const openPrivateSheet = (withHistory) => {
		setPrivateSheetHistory(Boolean(withHistory));
		setActiveSheet('private');
	};
	const openPrivate = () => {
		if (!railRoomy) {
			openPrivateSheet(false);
			return;
		}
		setPrivateRailHistory((open) => !open);
		const rail = document.getElementById('ld-private-rail');
		if (rail && rail.scrollIntoView) rail.scrollIntoView({ block: 'nearest' });
	};
	const togglePrivateRailHistory = () => {
		if (railRoomy) setPrivateRailHistory((open) => !open);
		else openPrivateSheet(true);
	};
	const privateOpen = railRoomy ? privateRailHistory : activeSheet === 'private';
	const songTab = board.currentSong && board.currentSong.id !== WARMUPS_SONG_ID ? board.currentSong : null;
	// A song just added: does another student already have it set up (tracks,
	// timed words, videos)? Looked up once per set-up song; the setup card
	// offers it before the lyric-video search (2026-11-01).
	const setupSong = setupSongId ? board.songs.find((s) => s.id === setupSongId) || null : null;
	const songReuse = useSongReuse({
		song: setupSong,
		studentId,
		studentName: studentName || null,
		setSongs: board.setSongs,
		reloadTracks: studentTracks.reload,
	});
	const songJobs = songTab ? jobsForSong(trackJobs, songTab) : [];
	const anyMacOn = macsOn(trackWorkers.workers, trackWorkers.now).length > 0;
	const dismissJob = (id) => setDismissedJobs((prev) => new Set([...prev, id]));
	// Open the panel on a mode: the column already shows it at 1200+, below
	// that it is the sheet.
	const openPanel = (mode) => {
		setSongPanelMode(mode);
		if (!isDesktop) setActiveSheet('song-panel');
	};
	// The strip's All tracks / Add a track, and the job chip.
	const openTracks = (target) => {
		if (target === 'add') setAddPreset({ nonce: Date.now() });
		openPanel('tracks');
	};
	// D3: "Track from Karaoke / Performance" opens Add a track at Options
	// with that video set, rather than queuing a job from the Videos mode.
	const VIDEO_LABELS = { lyrics: 'Lyrics', karaoke: 'Karaoke', performance: 'Performance' };
	const makeTrackFromVideo = (kind) => {
		const url = linkForSavedVideo(songTab?.videos?.[kind]);
		if (!url) return;
		setAddPreset({ url, removeVocals: removeVocalsForVideo(kind), label: `${VIDEO_LABELS[kind] || 'Saved'} video`, nonce: Date.now() });
		openPanel('tracks');
	};
	// F3: Try again on a failed add. A link re-opens Options with the same
	// link and vocals choice (one tap on Add track); an upload's file has to
	// be chosen again, so it opens at Source. The failed line is put away.
	const retryJob = (job) => {
		if (job && job.kind === 'link' && job.source && job.source.url) {
			setAddPreset({ url: job.source.url, removeVocals: Boolean(job.removeVocals), nonce: Date.now() });
		} else {
			setAddPreset({ nonce: Date.now() });
		}
		if (job) dismissJob(job.id);
		openPanel('tracks');
	};
	// Save as the student's key (step 4): only on a song tab with a student,
	// and only ever from a tap.
	const saveKey =
		studentId && songTab && currentUser?.uid
			? (plan) =>
					saveStudentKey({ studentId, studentName: studentName || null, song: songTab, plan, uid: currentUser.uid })
			: null;
	// "Save as a cut" (2026-09-28): the trimmed span of this tab's track, and
	// its Reference / Track / Vocals siblings, rendered as real files.
	const saveCut =
		studentId && songTab && currentUser?.uid
			? (file, span) =>
					saveTrackCut({
						studentId,
						studentName: studentName || null,
						song: songTab,
						set: cutSetFor(file, currentSongTracks),
						start: span.start,
						end: span.end,
						uid: currentUser.uid,
						lyricsOf: (f) => timedLyricsOf(f, currentSongTracks),
					})
			: null;
	// The song's key (2026-09-29, tracks/SongKeyLine.js): "Find the key"
	// queues the helper for one file; a teacher's correction is written by uid.
	const findKey =
		studentId && songTab && currentUser?.uid
			? (file, id) => findTrackKey({ id, file, studentId, studentName: studentName || null, song: songTab, uid: currentUser.uid })
			: null;
	const confirmKey = currentUser?.uid ? (id, key) => confirmTrackKey(id, key, currentUser.uid) : null;
	const saveKeyWho = (studentName || '').trim().split(/\s+/)[0] || null;
	// Lyrics that follow the track (2026-09-30, songpanel/lyricLineTimes.js):
	// the highlight follows only when the OPEN track belongs to this song and
	// carries timed words, so another song's track never drives these lyrics.
	// A tap seeks and, if paused, resumes, the way Apple Music plays from a
	// tapped line. With none of this song's tracks open (Anthony, same day:
	// "have tapping start the track too"), the lines are still tappable and a
	// tap starts the song's best timed track AT that line (pickLyricTrack).
	const openSongFile = trackPlayer.current && trackPlayer.current.engine === 'audio' && currentSongTracks.some((f) => trackPlayer.isCurrent(f)) ? trackPlayer.current.file : null;
	const openTimed = openSongFile ? timedLyricsOf(openSongFile, currentSongTracks) : null;
	const startFile = openTimed
		? null
		: pickLyricTrack(currentSongTracks, { timedOf: (f) => timedLyricsOf(f, currentSongTracks), playable: (f) => Boolean(playbackUrlFor(f)) });
	const startTimed = startFile ? timedLyricsOf(startFile, currentSongTracks) : null;
	// Pass 4 S4: this song's LYRIC video playing on the stage, with words
	// timed to the original recording. Below a playing track of this song
	// (whose own timing is exact for that file), above a merely-startable one.
	const lyricVideoId = songTab && songTab.videos && songTab.videos.lyrics ? getYoutubeVideoId(songTab.videos.lyrics) : null;
	const videoClock = stageVideo.current && lyricVideoId && stageVideo.current.videoId === lyricVideoId ? stageVideo.current : null;
	const songTimed = songTab && songTab.lyrics && Array.isArray(songTab.lyrics.timed) && songTab.lyrics.timed.length ? songTab.lyrics.timed : null;
	const trackPlaying = Boolean(openTimed && trackPlayer.isPlaying);
	const lyricFollow = videoClock && songTimed && !trackPlaying
		? {
				source: 'video',
				timed: songTimed,
				currentTime: videoClock.t,
				playing: videoClock.playing,
				onSeek: (t) => stageVideo.seek(lyricVideoId, t),
		  }
		: openTimed
		? {
				timed: openTimed.timed,
				currentTime: trackPlayer.currentTime,
				playing: trackPlayer.isPlaying,
				onSeek: (t) => {
					trackPlayer.seek(t);
					if (!trackPlayer.isPlaying) trackPlayer.toggle(openSongFile);
				},
		  }
		: startTimed
		? {
				timed: startTimed.timed,
				currentTime: null,
				playing: false,
				onSeek: (t) => trackPlayer.toggle(startFile, { at: t }),
		  }
		: null;
	// Now Playing (2026-10-29): the player pinned under the lyrics plays this
	// song's open track, else the one the lyrics would start, else its default cut.
	const npFile = songTab ? nowPlayingFile({ files: currentSongTracks, player: trackPlayer, song: songTab, startFile }) : null;
	const panelCounts = { tracks: currentSongTracks.length, videos: video.savedCount };
	// Pass 4 S2.1: the Tracks and Video rows state their live fact. A running
	// job beats a count, a failed add beats both; a saved lyric video with no
	// track offers Make a track; no video offers Find one.
	const liveJobs = visibleJobs(songJobs, trackWorkers.now || Date.now(), dismissedJobs).filter((j) => !j.kind || j.kind === 'link' || j.kind === 'transpose' || j.kind === 'cut');
	const runningJob = liveJobs.find((j) => jobTone(j) === 'working') || null;
	const failedAdd = liveJobs.find((j) => jobTone(j) === 'failed' && (!j.kind || j.kind === 'link')) || null;
	const playingHere = trackPlayer.current && currentSongTracks.some((f) => trackPlayer.isCurrent(f)) ? trackPlayer.current.file : null;
	const lyricVideo = songTab && songTab.videos && songTab.videos.lyrics ? songTab.videos.lyrics : null;
	const panelFacts = {
		tracks: runningJob
			? {
					text: jobStatusText(runningJob, { anyMacOn }),
					busy: true,
					progress: vocalProgress(runningJob, trackWorkers.now || Date.now()),
					action: canStopJob(runningJob)
						? {
								label: 'Stop',
								icon: 'fa-circle-stop',
								danger: true,
								onClick: () => {
									setStopAsk({ id: runningJob.id, nonce: Date.now() });
									openPanel('tracks');
								},
						  }
						: null,
			  }
			: failedAdd
			? { text: 'Track failed · Please tell Anthony right away', alert: true }
			: currentSongTracks.length === 0
			? {
					text: lyricVideo ? 'None yet · make one from the saved video' : 'None yet',
					action: lyricVideo && studentId ? { label: 'Make a track', icon: 'fa-wave-square', onClick: () => makeTrackFromVideo('lyrics') } : null,
			  }
			: {
					text: `${currentSongTracks.length} on this song${playingHere && trackPlayer.isPlaying ? ` · ${playingHere.variant || 'Track'} playing` : ''}`,
			  },
		videos: lyricVideo && videoClock
			? { text: `Lyric video ${videoClock.playing ? 'playing' : 'paused'} · ${Math.floor(videoClock.t / 60)}:${String(Math.floor(videoClock.t % 60)).padStart(2, '0')}` }
			: lyricVideo
			? { text: video.savedCount > 1 ? `Lyric video saved · ${video.savedCount} videos` : 'Lyric video saved' }
			: video.savedCount > 0
			? { text: `${video.savedCount} saved · no lyric video` }
			: {
					text: 'No video saved',
					action: {
						label: 'Find one',
						icon: 'fa-magnifying-glass',
						onClick: () => {
							openPanel('videos');
							video.edit && video.edit('lyrics');
						},
					},
			  },
	};
	const panelPanes = songTab
		? {
				lyrics: (
					<LyricsPane
						song={songTab}
						lyrics={lyrics}
						sections={songSections.isGeneric ? null : songSections.sections}
						activeSectionId={songSections.activeSection?.id || null}
						onSelectSection={songSections.setActive}
						follow={lyricFollow}
						nowPlaying={npFile ? { file: npFile, files: currentSongTracks, player: trackPlayer, onSaveKey: saveKey, saveKeyWho } : null}
						detach={
							isDesktop
								? {
										detached: lyricsDetached,
										onDetach: () => setLyricsDetached(true),
										onAttach: () => setLyricsDetached(false),
								  }
								: null
						}
						// The song-note field under the player in the detached window. The
						// handlers are WRAPPED, not passed: handleJot is declared further
						// down this component, and a const read above its declaration is
						// `undefined` here (CLAUDE.md, the use-before-declare trap). The
						// wrapper reads it when a note is sent, long after render.
						jot={{
							onSubmit: (text) => handleJot(text),
							onFirstJot: () => timer.startIfIdle(),
							placeholder: songSections.activeSection?.label ? `Note on ${songSections.activeSection.label}` : 'Note on this song',
						}}
						listening={songJobs.some((j) => j.kind === 'songLyrics' && (j.status === 'queued' || j.status === 'claimed'))}
						setup={
							// No `videos.lyrics` condition here: saving the video is step 1, and
							// the card must stay for step 2 (Yes / Later). It closes itself.
							songTab && setupSongId === songTab.id ? (
								<SongSetup song={songTab} video={video} videoTrack={videoTrack} reuse={songReuse} onDone={() => setSetupSongId(null)} />
							) : null
						}
					/>
				),
				tracks: (
					<TracksPane
						song={songTab}
						files={currentSongTracks}
						player={trackPlayer}
						roles={trackRoles}
						roleError={trackRoleError}
						onDismissRoleError={() => setTrackRoleError('')}
						hasStudent={Boolean(studentId)}
						jobs={songJobs}
						anyMacOn={anyMacOn}
						dismissed={dismissedJobs}
						onDismissJob={dismissJob}
						onRetryJob={retryJob}
						onStopJob={(job) => stopTrackJob(job.id)}
						stopAsk={stopAsk}
						nowMs={trackWorkers.now}
						add={
							studentId
								? {
										studentId,
										studentName: studentName || null,
										song: songTab,
										uid: currentUser?.uid || null,
										workers: trackWorkers.workers,
										now: trackWorkers.now,
										preset: addPreset,
								  }
								: null
						}
						tracksOnFile={tracksOnFile}
						onSetSectionMark={setSectionMarkFor}
						onSaveTrim={saveTrimFor}
						onSaveCut={saveCut}
						onFindKey={findKey}
						onConfirmKey={confirmKey}
						edit={
							canManageSongContent(userProfile)
								? {
										docs: trackDocsForSong(studentTracks.docs, board.currentSong),
										allDocs: studentTracks.docs,
										uid: currentUser?.uid || null,
										onChanged: studentTracks.reload,
								  }
								: null
						}
					/>
				),
				videos: <VideosPane song={songTab} video={video} videoTrack={videoTrack} onMakeTrack={makeTrackFromVideo} />,
		  }
		: {};

	const subLine = buildSubLine(lesson);

	// Register this lesson as the one that is open, so the portal topbar can
	// offer a way back into it from anywhere else (LessonReturnPill). Declared
	// AFTER studentName on purpose: Babel transpiles const to var for this
	// project's targets, so a read above the declaration yields `undefined`
	// rather than throwing, and the pill would silently lose its label
	// (CLAUDE.md — the use-before-declare trap).
	useEffect(() => {
		if (!lessonId || !lesson) return;
		setOpenLesson(lessonId, studentName);
	}, [lessonId, lesson, studentName, setOpenLesson]);

	// Leaving this lesson puts the stage back on the logo for the next one
	// (2026-10-02): on unmount (Complete, Student didn't come, any route
	// change), on a switch to another lesson, and when the tab closes, which
	// runs no React cleanup.
	useEffect(() => {
		window.addEventListener('pagehide', resetStage);
		return () => {
			window.removeEventListener('pagehide', resetStage);
			resetStage();
		};
	}, [lessonId]);

	// Remember which song tab was open, so coming back lands where they left.
	useEffect(() => {
		if (board.currentSongId) patchScratch({ currentSongId: board.currentSongId });
	}, [board.currentSongId, patchScratch]);

	// Link a student to an unlinked lesson, then flush any locally-held jots.
	const handleLinkStudent = useCallback(
		async (selected) => {
			if (!selected || !lessonId) return;
			const fullName =
				selected.fullName || `${selected.firstName || ''} ${selected.lastName || ''}`.trim();
			await updateDoc(doc(db, 'lessons', lessonId), {
				studentId: selected.id,
				studentName: fullName,
				lastUpdated: Timestamp.now(),
			});
			setStudent(selected);
			setLesson((prev) => (prev ? { ...prev, studentId: selected.id, studentName: fullName } : prev));
			// Persist notes jotted while unlinked. flushLocal works off the
			// pre-link board closure; the studentId flip separately re-hydrates
			// the board from Firestore, and those two race. Re-hydrating again
			// AFTER the flush lands the board on what was actually written —
			// including the repertoire rows the flush just ensured for songs
			// added while unlinked.
			await notes.flushLocal({ studentId: selected.id });
			// Same repair, for recaps rather than song notes: any tier already
			// saved on this lesson was written with `studentId: null` and is
			// unreadable by the family until it is stamped. Linking the student
			// used to fix the lesson and leave those recaps orphaned forever.
			await recap.backfillStudentId(selected.id);
			await board.reload();
			bumpCarryForward();
		},
		[lessonId, notes, recap, board, bumpCarryForward]
	);

	// Jot: first jot auto-starts a forgotten timer; the note is born captured.
	// …and lands in the active song section, if one is picked (2026-09-25).
	const handleJot = useCallback(
		(text) => {
			setHasJotted(true); // pass 3 C3: the first jot collapses Carry-forward
			return notes.addNote(text, null, songSections.activeSection);
		},
		[notes, songSections.activeSection]
	);
	const sectionChips = (
		<SectionChips
			sections={songSections.sections}
			activeId={songSections.activeSection?.id || null}
			onSelect={songSections.setActive}
			status={songSections.status}
			isGeneric={songSections.isGeneric}
			onManage={() => (activeSheet === 'song-panel' ? setSectionsOverLyrics(true) : setActiveSheet('sections'))}
		/>
	);
	const hasSectionChips = songSections.sections.length > 0;

	// Deliver dialog → the one legal captured→delivered crossing.
	//
	// Pass 3 C4: note rows AND Carry-forward items open the dialog through one
	// hook, which also writes a Carry-forward note whose song is not on the
	// board (useDeliverNote.js has the reasoning).
	const {
		deliverNote,
		deliverCtx,
		carryError,
		openDeliver,
		closeDeliver,
		handleDeliver,
		clearCarryError,
	} = useDeliverNote({
		board,
		notes,
		lessonId,
		uid: currentUser?.uid || null,
		onPersisted: bumpCarryForward,
	});
	// One object for both Arrive mounts (the rail and the notes column).
	const arrive = {
		onCarryAct: openDeliver,
		carryError,
		onDismissCarryError: clearCarryError,
		familyQuestions,
	};

	const completeLesson = useCallback(async () => {
		const ok = await timer.complete();
		if (ok) {
			// The lesson is over: drop the unsent scratch and take the "back to
			// the lesson" pill down. Leaving either behind would point a teacher
			// at a finished lesson for the rest of the day.
			clearScratch();
			clearOpenLesson();
			setTimeout(() => navigate('/portal/work/today'), 600);
		}
		return ok;
	}, [timer, navigate, clearScratch, clearOpenLesson]);

	// The student never arrived: mark the record no_show and go back to Today.
	// Deliberately NOT a completion — nothing was taught, the timer stays as it
	// is, and no recap is owed. (Attendance arc, Phase A.)
	const markNoShow = useCallback(async () => {
		if (!lessonId) return;
		try {
			await setLessonOutcome({ row: { lessonDocId: lessonId }, value: 'no_show', uid: auth.currentUser?.uid || null });
			setLesson((prev) => (prev ? { ...prev, outcome: 'no_show' } : prev));
			setActiveSheet(null);
			navigate('/portal/work/today');
		} catch (error) {
			console.error('Error marking no-show:', error);
		}
	}, [lessonId, setLesson, navigate]);

	// Recap email recipient — students.parent1.email, NOT families. parent1 is
	// written by both real creation paths (CSV import, convertInquiryToStudents),
	// while `families` reads are admin/owner-only in the rules, so resolving
	// through familyId would silently break for an employee teacher.
	const recapRecipient = student?.parent1?.email?.trim() || '';
	const recapRecipientName =
		student?.parent1?.firstName?.trim() || student?.parentName || '';

	const sendRecapEmail = useCallback(async () => {
		const payload = buildRecapEmailPayload({
			everyone: recap.tierText('student'),
			parents: recap.tierText('parent'),
			recipient: recapRecipient,
			studentName: student?.fullName || lesson?.studentName || '',
			lessonDate: lesson?.date || '',
		});
		if (!payload) return;
		const result = await sendLessonNotes(payload);
		if (result?.skipped) {
			// The family muted lesson recaps — their choice, not a failure.
			console.log('Recap email skipped: family opted out of lesson recaps.');
		}
	}, [recap, recapRecipient, student, lesson]);

	// Quick-add task from the ⋯ drawer: the student is auto-attached as context,
	// the teacher as provenance. Area/priority stay unset so it lands in triage
	// like any capture (createTask defaults triaged:false when no area is set).
	const handleCreateTask = useCallback(
		async ({ title, notes }) => {
			await createTask({
				title,
				description: notes || '',
				studentId: student?.id || lesson?.studentId || null,
				studentName: studentName || null,
				source: 'manual',
				createdBy: currentUser?.uid || null,
				createdByName:
					userProfile?.displayName ||
					currentUser?.displayName ||
					currentUser?.email ||
					null,
			});
		},
		[student, lesson, studentName, currentUser, userProfile]
	);

	if (lessonDenied) {
		return <AccessNotice title="This lesson isn't shared with you" />;
	}
	if (loading) {
		return (
			<div className="ld-loading">
				<i className="fa-sharp fa-solid fa-spinner fa-spin" aria-hidden="true" /> Loading lesson…
			</div>
		);
	}
	if (!lesson) {
		return <AccessNotice title="Lesson not found" />;
	}

	return (
		<div className="ld-shell">
			{recap.recoveryAvailable && (
				<div className="ld-recovery">
					<span>
						<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" /> Unsaved
						recap from an earlier session was found.
					</span>
					<span className="ld-recovery__actions">
						<button type="button" onClick={recap.restoreFromBackup}>
							Restore
						</button>
						<button type="button" className="ld-recovery__dismiss" onClick={recap.dismissRecovery}>
							Dismiss
						</button>
					</span>
				</div>
			)}

			{timer.writeError && (
				<div className="ld-timer-error" role="alert">
					{/* Pass 3 A8: every teacher-visible failure LEADS with the ask,
					    the hook's own words small underneath (the owner's rule). */}
					<span className="ld-timer-error__text">
						<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" />{' '}
						<strong>Please tell Anthony right away.</strong>
						<span className="ld-timer-error__detail">{timer.writeError}</span>
					</span>
					<button
						type="button"
						className="ld-timer-error__dismiss"
						aria-label="Dismiss"
						onClick={timer.clearWriteError}>
						<i className="fa-sharp fa-solid fa-xmark" aria-hidden="true" />
					</button>
				</div>
			)}

			<LessonTopbar
				studentName={studentName}
				subLine={subLine}
				timer={timer.timer}
				onStartTimer={timer.start}
				onOpenDrawer={() => setActiveSheet('drawer')}
				onOpenRecap={openRecap}
				recapSaveState={recapSaveState}
				onOpenPrivate={student ? openPrivate : null}
				privateCount={student && !privateNotes.isLoading && !privateNotes.error ? privateNotes.entries.length : null}
				privateOpen={privateOpen}
			/>

			{showStrip && <ConductingStrip wp={wp} />}
			{/* Pass 3 E1: the song tab's sibling of the conducting strip, same
			    slot. Its transport follows what is PLAYING (E5), its pills are
			    this tab's cuts. */}
			{songTab && (
				<TrackStrip
					song={songTab}
					files={currentSongTracks}
					player={trackPlayer}
					trackDocs={studentTracks.docs}
					jobs={songJobs}
					anyMacOn={anyMacOn}
					dismissed={dismissedJobs}
					nowMs={trackWorkers.now}
					onOpenTracks={openTracks}
					stage={{
						hasLyrics: stageLyrics.hasLyrics,
						videos: video.videos,
						songTitle: songTab.metadata?.title || songTab.name || '',
					}}
				/>
			)}

			<div className={`ld-body${showStrip ? ' ld-body--strip' : ''}`}>
				<ContextRail
					student={student}
					privateFile={{
						...privateFileProps,
						historyOpen: railRoomy && privateRailHistory,
						onToggleHistory: togglePrivateRailHistory,
					}}
				/>

				{/* The centre track owns BOTH the scroll region and the jot bar, so
				    the bar is the notes column's exact width by construction rather
				    than by pixel constants (see JotBar.css → .ld-jotbar--main). On
				    phone .ld-center is display:contents, which removes this box
				    entirely and leaves the bar viewport-fixed in the thumb zone
				    exactly as before. */}
				<div className="ld-center">
					<main className={`ld-main${hasSectionChips ? ' ld-main--sections' : ''}`}>
						<NotesColumn
							canManageNotes={canManageSongContent(userProfile)}
							warmups={warmups}
							deckInStrip={showStrip}
							student={student}
							carryForward={carryForward}
							board={board}
							notes={notes}
							teacherNames={{}}
							currentTeacherId={currentUser?.uid || null}
							onDeliver={openDeliver}
							arrive={arrive}
							onAddSong={() => setActiveSheet('add-song')}
							lessonId={lessonId}
							setupRow={
								// Below 1200 there is no column: the setup is one row under the
								// tabs, and Pick opens the song sheet on it (pass 4 S6.1).
								!isDesktop && songTab && setupSongId === songTab.id && !(songTab.videos && songTab.videos.lyrics) ? (
									<div className="ld-setuprow" role="group" aria-label={`Setting up ${songTab.name}`}>
										{songReuse.status === 'ready' ? (
											<span className="ld-setuprow__text">
												<i className="fa-sharp fa-solid fa-clone" aria-hidden="true" />
												{songTab.name} is already set up for another student
											</span>
										) : (
											<span className="ld-setuprow__text">
												<i className="fa-sharp fa-solid fa-video" aria-hidden="true" />
												Setting up {songTab.name}: pick a lyric video
											</span>
										)}
										<button type="button" className="ld-setup__btn ld-setup__btn--primary" onClick={() => openPanel('lyrics')}>
											{songReuse.status === 'ready' ? 'See' : 'Pick'}
										</button>
										<button type="button" className="ld-setup__btn" onClick={() => setSetupSongId(null)}>
											Later
										</button>
									</div>
								) : null
							}
							songTabs={{
								opened: scratch.openSongIds || [],
								closed: scratch.closedSongIds || [],
								open: (id) =>
									patchScratch({
										openSongIds: [...new Set([...(scratch.openSongIds || []), id])],
										closedSongIds: (scratch.closedSongIds || []).filter((x) => x !== id),
									}),
								close: (id) =>
									patchScratch({
										closedSongIds: [...new Set([...(scratch.closedSongIds || []), id])],
										openSongIds: (scratch.openSongIds || []).filter((x) => x !== id),
									}),
							}}
							onOpenPanel={openPanel}
							panelCounts={panelCounts}
							previousRecaps={previousRecaps}
							onOpenPreviousRecaps={openPreviousRecaps}
							carryOpen={carryOpen}
							onToggleCarry={toggleCarry}
							sections={songSections.sections}
						/>
					</main>

					<div className="ld-jotbar--main">
						{sectionChips}
						<JotBar
							onSubmit={handleJot}
							onFirstJot={timer.startIfIdle}
							draft={scratch.jotDraft}
							onDraftChange={(text) => patchScratch({ jotDraft: text })}
						/>
					</div>
				</div>

				{/* THE RIGHT COLUMN IS DESKTOP-ONLY, and until I1 phase 3 it was not
				    gated at all — it rendered at every width, side-by-side at iPad
				    (squeezing the notes track to 212px at 768, the standing report on
				    card AaHwhlBhztwuqQxGF91x) and as a stray 320px block at the bottom
				    of a phone lesson. Below 1200 its contents are reached from the
				    tool row instead, as sheets. Gated in JS here AND hidden in CSS as
				    a backstop, same two-guard pattern as the conducting strip. */}
				{(isDesktop || (hasRoutineColumn && isWarmupsTab)) && (
				<RightColumn
					warmups={warmups}
					deckInStrip={showStrip}
					song={board.currentSong}
					songPanel={
						songTab ? (
							<SongPanel
								song={songTab}
								mode={songPanelMode}
								onMode={setSongPanelMode}
								facts={panelFacts}
								panes={panelPanes}
							/>
						) : null
					}
				/>
				)}
			</div>

			{deliverNote && (
				<DeliverDialog
					note={deliverNote}
					studentName={student?.firstName || studentName || 'the student'}
					context={deliverCtx}
					preselect={deliverCtx?.preselect || null}
					onDeliver={handleDeliver}
					onClose={closeDeliver}
				/>
			)}

			{activeSheet === 'add-song' && (
				<AddSongSheet
					onAdd={async (name, metadata) => {
						const res = await board.addSong(name, metadata);
						// An added song stays a pill for the rest of the lesson.
						if (res && res.id) patchScratch({ openSongIds: [...new Set([...(scratch.openSongIds || []), res.id])] });
						// A NEW song with no lyric video yet starts its setup (pass 4 S3).
						if (res && res.id && !res.existed) setSetupSongId(res.id);
						return res;
					}}
					studentId={studentId}
					onClose={() => setActiveSheet(null)}
				/>
			)}

			{/* Pass 3 D2: the Song panel as a sheet below 1200 (replaces the old
			    LyricsSheet). The phone sheet keeps its own chips + jot bar; the
			    iPad side sheet does not (the page's jot bar is beside it). A3's
			    stacked Sections sheet now stacks on THIS sheet. */}
			{activeSheet === 'song-panel' && songTab && !isDesktop && (
				<SongPanelSheet
					song={songTab}
					mode={songPanelMode}
					onMode={setSongPanelMode}
					facts={panelFacts}
					panes={panelPanes}
					lyrics={lyrics}
					withJot={isPhoneWidth}
					sectionChips={hasSectionChips ? sectionChips : null}
					onJot={handleJot}
					onFirstJot={timer.startIfIdle}
					onClose={() => {
						setSectionsOverLyrics(false);
						setActiveSheet(null);
					}}
				/>
			)}

			{(activeSheet === 'sections' || (activeSheet === 'song-panel' && sectionsOverLyrics)) && (
				<SectionsSheet
					song={board.currentSong}
					sections={songSections.sections}
					isGeneric={songSections.isGeneric}
					status={songSections.status}
					canDetect={songSections.canDetect}
					onRename={songSections.rename}
					onDetectAgain={songSections.detectAgain}
					onClose={() => (sectionsOverLyrics ? setSectionsOverLyrics(false) : setActiveSheet(null))}
				/>
			)}

			{/* THE ONE Recap overlay — a right-edge drawer on desktop, a bottom sheet
			    below 1200px — reached from EXACTLY TWO controls since design pass 2
			    batch B (change 7): the topbar's Recap button (every width, every
			    tab) and the Last lesson row (rail, or the notes column when the
			    rail is hidden), which lands on the Previous recaps disclosure; plus
			    the `?open=recap` deep link, which goes through the topbar's opener.
			    The tool-row Recap (<1200, song tabs) and the right column's
			    disclosure row (desktop) are gone. It is rendered HERE and nowhere
			    else on purpose: until 2026-09-13 the right column mounted its own
			    RecapDrawer, so two entry points could put two editors per tier on
			    one screen — exactly the hazard WrapUpSheet's comment about "a
			    SECOND editor per tier" describes. One state, one instance;
			    recap/singleRecapDrawer.test.js pins it as source text and
			    recap/recapEntryPoints.test.js pins the entry-point count.
			    Its footer carries Complete lesson (change 10): setting 'wrap-up'
			    swaps this overlay for the existing WrapUpSheet, unchanged. */}
			{activeSheet === 'recap' && (
				<RecapDrawer
					variant={isDesktop ? 'drawer' : 'sheet'}
					recap={recap}
					students={students}
					studentName={student?.firstName || studentName}
					parentName={student?.parentName || ''}
					previous={previousRecaps}
					previousOpen={recapFocus === 'previous'}
					saveState={recapSaveState}
					emailRecipient={recapRecipient}
					emailRecipientName={recapRecipientName}
					onComplete={() => setActiveSheet('wrap-up')}
					onClose={closeSheet}
					// P5.2: the drawer's pointer to the Private file. Close the drawer
					// first, so the rail (or the private sheet) is what comes next.
					onOpenPrivate={
						student
							? () => {
									closeSheet();
									openPrivate();
							  }
							: null
					}
				/>
			)}

			{/* The Private file wherever the rail is hidden or too narrow (pass 4
			    P4.1 / P4.2): the Recap drawer's right-hand geometry at 440 from
			    768 up, a bottom sheet on phone. Same PrivateFile as the rail. */}
			{activeSheet === 'private' && student && (
				<BottomSheet
					title="Private file"
					titleId="ld-private-sheet-title"
					className={`ld-sheet--private${isPhoneWidth ? '' : ' ld-sheet--drawer'}`}
					onClose={closeSheet}>
					<PrivateFile
						studentId={student.id}
						variant="sheet"
						headingId="ld-private-sheet-title"
						{...privateFileProps}
						historyOpen={privateSheetHistory}
						onToggleHistory={() => setPrivateSheetHistory((open) => !open)}
					/>
				</BottomSheet>
			)}

			{activeSheet === 'drawer' && (
				<StudentDrawer
					student={student}
					studentName={studentName}
					subLine={subLine}
					students={students}
					board={board}
					onLinkStudent={handleLinkStudent}
					onAddTask={() => {
						setActiveSheet(null);
						setTaskDialogOpen(true);
					}}
					onMarkNoShow={markNoShow}
					tracksOnFile={tracksOnFile}
					player={trackPlayer}
					onClose={() => setActiveSheet(null)}
				/>
			)}

			<QuickAddTaskDialog
				open={taskDialogOpen}
				studentName={student?.firstName || studentName}
				onCreate={handleCreateTask}
				onClose={() => setTaskDialogOpen(false)}
			/>

			{activeSheet === 'wrap-up' && (
				<WrapUpSheet
					warmupDraft={warmupRoutine}
					boardCounts={notes.boardCounts}
					timerLabel={timer.timer.label}
					recap={recap}
					students={students}
					studentName={student?.firstName || studentName}
					parentName={student?.parentName || ''}
					emailRecipient={recapRecipient}
					emailRecipientName={recapRecipientName}
					onSendRecapEmail={sendRecapEmail}
					onComplete={completeLesson}
					// Pass 3 A4 + B3: the Recap drawer is Wrap up's ONLY opener, so
					// Keep teaching always goes back to it.
					backToRecap
					onKeepTeaching={openRecap}
					onClose={closeSheet}
				/>
			)}
		</div>
	);
};

export default LessonDashboard;
